import { useEffect, useState, memo, useRef } from "react";
import { Alert, Box, Button, Drawer, Grid, Snackbar } from "@mui/material";
import LoadingComponent from "../LoadingComponent.jsx";

import Shopify from "./Shopify.jsx";
import Woocommerce from "./Woocommerce.jsx";
import Prestashop from "./Prestashop.jsx";
import Meli from "./Meli.jsx";
import Bootic from "./Bootic.jsx";
import Vtex from "./Vtex.jsx";
import EcommerceItem from "./helpers/EcommerceItem.jsx";
import EcommerceSelection from "./helpers/EcommerceSelection.jsx";

import { useEcommerceIntegrations } from "../../hooks/useEcommerceIntegrations.jsx";

const MainEcommerce = () => {
  const {
    integrations,
    warehouses,
    loading,
    error,
    availableEcommerces,
    fetchIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    initMeliAuth,
    initShopifyAuth,
  } = useEcommerceIntegrations();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedEcommerce, setSelectedEcommerce] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [snackAlert, setSnackAlert] = useState({ active: false, message: "" });

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleOpenConfig = (ecommerce, edit = false) => {
    setSelectedEcommerce(ecommerce);
    setIsEdit(edit);
    setOpenDrawer(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta integración?"))
      return;

    const result = await deleteIntegration(id);
    if (result.success) {
      setSnackAlert({
        open: true,
        msg: "Eliminado correctamente",
        type: "success",
      });
    } else {
      setSnackAlert({ open: true, msg: result.error, type: "error" });
    }
  };

  const handleSave = async (formData) => {
    let result;
    if (isEdit) {
      result = await updateIntegration(formData.id, formData);
    } else {
      result = await createIntegration(formData);
    }

    if (result.success) {
      setSnackAlert({
        open: true,
        msg: "Guardado exitosamente",
        type: "success",
      });
      setOpenDrawer(false);
    } else {
      setSnackAlert({ open: true, msg: result.error, type: "error" });
    }
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);

    setTimeout(() => {
      setSelectedEcommerce(null);
      setIsEdit(false);
    }, 300);
  };

  const renderEcommerceForm = () => {
    if (!selectedEcommerce) return null;

    const props = {
      warehouses,
      loading,
      ecommerce: selectedEcommerce,
      onSave: handleSave,
      onClose: handleCloseDrawer,
      isEdit: isEdit,
    };

    switch (selectedEcommerce.name.toLowerCase()) {
      case "shopify":
        return <Shopify {...props} onConnect={initShopifyAuth} />;
      case "woocommerce":
        return <Woocommerce {...props} />;
      case "prestashop":
        return <Prestashop {...props} />;
      case "mercado libre":
        return (
          <Meli {...props} onConnect={initMeliAuth} />
        );
      case "bootic":
        return <Bootic {...props} />;
      case "vtex":
        return <Vtex {...props} />;
      default:
        return (
          <Alert severity="warning">
            Configuración no disponible para {selectedEcommerce.name}
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {loading && <LoadingComponent />}
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ maxWidth: 800 }}>
        {integrations.map((ecommerce) => (
          <EcommerceItem
            key={ecommerce.id}
            ecommerce={ecommerce}
            logoSrc={ecommerce.image}
            onOpenConfig={() => handleOpenConfig(ecommerce, true)}
            onDeleteClick={() => handleDelete(ecommerce.id)}
          />
        ))}
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => setOpenDrawer(true)}
        >
          Agregar Nueva Integración
        </Button>
      </Box>

      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 2,
          }}
        >
          {selectedEcommerce && !isEdit && (
            <Button
              onClick={() => setSelectedEcommerce(null)}
              size="medium"
              startIcon={<span>←</span>}
              sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
            >
              Volver al listado
            </Button>
          )}
          <Button
            onClick={handleCloseDrawer}
            color="inherit"
            size="small"
            sx={{ textTransform: "none", minWidth: "auto" }}
          >
            Cerrar
          </Button>
        </Box>
        <Box sx={{ width: { xs: "100vw", sm: 550 }, p: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            {!selectedEcommerce ? (
              <EcommerceSelection
                integrations={availableEcommerces}
                handleSelectNew={handleOpenConfig}
              />
            ) : (
              <>{renderEcommerceForm()}</>
            )}
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={snackAlert.active}
        autoHideDuration={4000}
        onClose={() => setSnackAlert({ ...snackAlert, active: false })}
        message={snackAlert.message}
      />
    </Box>
  );
};

export default memo(MainEcommerce);
